// A problem of a whole analysis, opened: the same solution as in «Páginas → Ver solución» (see ProblemSolution in
// element-guide.tsx): the problem, the element it is about with the move between its elements, the solution, «Ver
// solución técnica» and «Estado de la incidencia», the last two closed. A problem spans pages and its elements and
// guidance are read per page, so the page is chosen first (the one that looks after a shared component, when it is
// one) and the page's own data is loaded when the card opens. The capture is one step away, on the page.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pageQuery, type ScanDetail } from '../api';
import { elementsOf } from '../element-logic';
import { groupProblems, type Problem } from '../results-logic';
import { Message } from '../ui';
import { pathOf } from '../util';
import { ProblemSolution } from './element-guide';
import type { Rights } from './follow-state';

/** The pages the problem is on, the one that looks after a shared component first. */
function affectedPages(problem: Problem, scan: ScanDetail): string[] {
  const keys = [...new Set(problem.issues.flatMap((issue) => issue.pageKeys))];
  const owner = problem.shared.find((component) => component.ownerKey)?.ownerKey;
  const known = keys.filter((key) => scan.pages.some((page) => page.key === key));
  return owner && known.includes(owner) ? [owner, ...known.filter((key) => key !== owner)] : known;
}

export function ScanProblemSolution({
  problem,
  scan,
  siteId,
  rights,
}: {
  problem: Problem;
  scan: ScanDetail;
  siteId: string;
  rights: Rights;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const keys = useMemo(() => affectedPages(problem, scan), [problem, scan]);
  const [pageKey, setPageKey] = useState(keys[0]);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const detail = useQuery({ ...pageQuery(scan.id, pageKey ?? ''), enabled: Boolean(pageKey) });
  const page = scan.pages.find((candidate) => candidate.key === pageKey);
  const pageProblem = useMemo(
    () =>
      detail.data ? groupProblems(detail.data.issues).find((item) => item.key === problem.key) : undefined,
    [detail.data, problem.key],
  );
  const elements = useMemo(() => (pageProblem ? elementsOf(pageProblem) : []), [pageProblem]);

  if (!pageKey || !page) return null;
  const name = (key: string) => {
    const found = scan.pages.find((candidate) => candidate.key === key);
    return found?.title || (found ? pathOf(found.finalUrl) : key);
  };
  const goToPage = () =>
    navigate({
      to: '/sites/$siteId/paginas',
      params: { siteId },
      search: { scan: scan.id, p: pageKey, k: problem.key, e: selected ?? elements[0]?.id },
    });
  return (
    <div className="issue-solution">
      {keys.length > 1 && (
        <label className="issue-page-select" htmlFor={`pagina-${problem.key}`}>
          <span>{t('results.issue.pageOf', { count: keys.length })}</span>
          <select
            id={`pagina-${problem.key}`}
            value={pageKey}
            onChange={(event) => {
              // Another page starts the elements from the first one.
              setPageKey(event.target.value);
              setSelected(undefined);
            }}
            className="min-h-11 w-full rounded-md border border-field-border bg-paper px-3 py-2 text-ink"
          >
            {keys.map((key) => (
              <option key={key} value={key}>
                {name(key)}
              </option>
            ))}
          </select>
        </label>
      )}
      {detail.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : detail.isError ? (
        <Message tone="error">{t('errors.generic')}</Message>
      ) : pageProblem ? (
        <ProblemSolution
          // Another page starts everything from scratch.
          key={pageKey}
          problem={pageProblem}
          elements={elements}
          selectedId={selected}
          onSelect={setSelected}
          device="escritorio"
          scan={scan}
          page={page}
          siteId={siteId}
          rights={rights}
          imageHeight={null}
          // No capture here: «Ver dónde está» takes the person to the page, with this problem and element opened.
          stacked
          inCapture={false}
          onShowCapture={goToPage}
          bulk={{ target: { issueIds: problem.issues.map((issue) => issue.id) }, count: problem.elements }}
          analysisElements={problem.elements}
        />
      ) : (
        <p>{t('results.issue.noPageData')}</p>
      )}
    </div>
  );
}
